import { SiteContainer } from "@/src/components/SiteContainer/SiteContainer";
import React, { FunctionComponent } from "react";
import HealthTrait, { BreedTrait } from "@/src/components/organisms/BreedTraitDetail/BreedTraitDetail";
import BreedHero, { IBreedHero } from "@/src/components/organisms/BreedHero";
import { IBreadCrumb } from "@/src/components/molecules/BreadCrumbs";
import { Document } from "@contentful/rich-text-types/dist/types/types";
import Container from "@/src/components/atoms/Container/Container";
import BreedDetailHeader from "@/src/components/molecules/BreedDetailHeader";
import { RichTextContent } from "@/src/components/v2/RichTextContent/RichTextContent";
import { Article } from "schema-dts";
import { StructuredData } from "@/src/components/StructuredData";
import { Utils } from "../Utils";

export interface BreedDetailPageProps extends IBreedHero {
    seoTitle?: string;
    seoDescription?: string;

    speciesName: string;
    speciesSlug: string;
    breedSlug: string;

    healthTraits?: BreedTrait[];
    personalityContent?: Document;
    careContent?: Document;
    removeHreflang?: boolean;
    conclusion?: Document;
    /** Contentful entry dates (sys.createdAt / sys.updatedAt), for the Article node. */
    publishTimestamp?: string;
    updateTimestamp?: string;
}

const BreedDetailPage: FunctionComponent<BreedDetailPageProps> = props => {
    const breadcrumbs: IBreadCrumb[] = [
        { text: `Breeds`, url: "/breeds", isActive: false },
        { text: props.speciesName + " Breeds", url: `/breeds/${props.speciesSlug}-breeds`, isActive: false },
        {
            text: props.name,
            url: `/breeds/${props.speciesSlug}-breeds/${props.breedSlug}`,
            isActive: true
        }
    ];

    const article: Article = {
        "@type": "Article",
        // The breed name, as BreedHero shows it, not the page title: that is now always set and can carry the brand suffix
        headline: props.name,
        image: props.imageURL ? [props.imageURL] : [],
        datePublished: props.publishTimestamp,
        dateModified: props.updateTimestamp,
        // Breed guides have no byline in Contentful, so the organisation is both author and publisher, by @id (ENG-19525).
        // The organisation node itself is emitted below so the reference resolves on this page.
        ...Utils.generateArticleAuthorship([])
    };

    return (
        <SiteContainer
            title={props.seoTitle ?? props.name}
            breadcrumbs={breadcrumbs}
            description={props.seoDescription}
            featuredImageURL={props.imageURL}
            removeHreflang={props.removeHreflang}
        >
            <StructuredData data={Utils.generateOrganizatonSchema()} />
            <StructuredData data={article} />
            <div className="mt-6">
                <BreedHero
                    imageURL={props.imageURL}
                    name={props.name}
                    description={props.description}
                    traits={props.traits}
                    healthRating={props.healthRating}
                    personalityRating={props.personalityRating}
                    careRating={props.careRating}
                    height={props.height}
                    weight={props.weight}
                    lifespan={props.lifespan}
                />
            </div>
            <div>
                {props.healthTraits && <HealthTrait traits={props.healthTraits} type="health" starCount={props.healthRating} specie={props.speciesSlug} />}

                {props.personalityContent && (
                    <Container>
                        <div className="border-none border-border-dark md:mb-6 md:border-b md:border-solid md:pb-6 lg:mb-10 lg:pb-10">
                            <BreedDetailHeader trait="personality" starCount={props.personalityRating as any} />
                            <div className="breedTraits mt-4 lg:mt-8">
                                <RichTextContent demoteHeading1 content={props.personalityContent} />
                            </div>
                        </div>
                    </Container>
                )}
                {props.careContent && (
                    <Container>
                        <div className="border-none border-border-dark md:mb-6 md:border-b md:border-solid md:pb-6 lg:mb-10 lg:pb-10">
                            <BreedDetailHeader trait="lifetime-care" starCount={props.careRating as any} />
                            <div className="breedTraits mt-4 lg:mt-8">
                                <RichTextContent demoteHeading1 content={props.careContent} />
                            </div>
                        </div>
                    </Container>
                )}
                {props.conclusion && (
                    <Container>
                        <div className="breedTraits border-none border-border-dark md:mb-6 md:border-b md:border-solid md:pb-6 lg:mb-10 lg:pb-10">
                            <RichTextContent demoteHeading1 content={props.conclusion} />
                        </div>
                    </Container>
                )}
            </div>
        </SiteContainer>
    );
};

export default BreedDetailPage;
